/**
 * @file Gains hors-ligne : calcule un rapport au chargement (absence >= minAwaySeconds), le stocke en attente, credite a la reclamation.
 */
import { Decimal } from '@shared/bignum';
import { fail, okVoid } from '@shared/types';
import type { Result } from '@shared/types';
import { BIOME_ORDER } from '@config/biomes';
import type { SimContext } from '../Context';
import { simulateOffline } from '../balance/OfflineSimulator';
import { biomeForStageValue } from '../balance/formulas';
import type { OfflineReport } from '../state/PlayerData';

/** Cout (Dia) du bouton "x2" du rapport hors-ligne (pas de pub dans ce jeu). */
const DOUBLE_COST_DIA = 20;

export class OfflineService {
  constructor(private readonly ctx: SimContext) {}

  get pending(): OfflineReport | null {
    return this.ctx.data.offline.pending;
  }

  /**
   * Appele au chargement / retour d'onglet : absence >= minAwaySeconds => regeneration des tickets + gains analytiques du farm du
   * niveau courant (equipe actuelle, buffs exclus), cumules dans un rapport en attente.
   */
  computeOnResume(nowMs: number): OfflineReport | null {
    const ctx = this.ctx;
    const bal = ctx.config.balance;
    const awaySeconds = (nowMs - ctx.data.meta.lastSeenMs) / 1000;
    if (awaySeconds < bal.offline.minAwaySeconds) return null;
    const ticketsRegenerated = ctx.services.tickets.regen(nowMs);
    const run = ctx.data.run;
    const biome = ctx.config.biomes[biomeForStageValue(bal, run.stageValue, BIOME_ORDER)];
    const out = simulateOffline({
      bal,
      awaySeconds,
      teamDps: ctx.weaponStats.teamDps(ctx.data, 'main', true),
      moneyMult: ctx.stats.get('money', { mode: 'main', excludeBuffs: true }),
      capHours: ctx.stats.getNumber('offlineHours'),
      efficiency: ctx.stats.getNumber('offlineRate'),
      stageValue: run.stageValue,
      treeTable: biome.treeTable.map((e) => ({ def: ctx.index.target(e.targetId), weight: e.weight })),
    });
    const prev = ctx.data.offline.pending;
    const report: OfflineReport = {
      awaySeconds: awaySeconds + (prev?.awaySeconds ?? 0),
      simulatedSeconds: out.simulatedSeconds + (prev?.simulatedSeconds ?? 0),
      capped: out.capped || (prev?.capped ?? false),
      money: out.money.plus(prev?.money ?? 0),
      kills: out.kills + (prev?.kills ?? 0),
      ticketsRegenerated: ticketsRegenerated + (prev?.ticketsRegenerated ?? 0),
    };
    ctx.data.offline.pending = report;
    ctx.data.meta.lastSeenMs = nowMs;
    ctx.bus.emit('OfflineComputed', { report });
    return report;
  }

  /** Credite le rapport (x2 pour 20 Dia). L'argent passe par wallet.grant (la stat money est deja incluse dans le rapport). */
  claim(doubleWithDia: boolean): Result {
    const ctx = this.ctx;
    const report = ctx.data.offline.pending;
    if (!report) return fail('nothingToClaim');
    const grant = (): Result => {
      const money = doubleWithDia ? report.money.times(2) : report.money;
      ctx.wallet.grant({ money }, 'offline');
      ctx.data.run.moneyAccum = ctx.data.run.moneyAccum.plus(money);
      ctx.data.progress.totalKills += report.kills;
      ctx.data.offline.pending = null;
      return okVoid;
    };
    return doubleWithDia ? ctx.tx.purchase({ dia: new Decimal(DOUBLE_COST_DIA) }, 'offlineDouble', grant) : grant();
  }
}
